import { useState, useRef, useCallback, ReactNode } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { triggerHaptic } from '@/hooks/useHaptics';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: ReactNode;
  className?: string;
  /** Extra classes applied to the inner scroll container (motion.div) */
  scrollClassName?: string;
  /** Exposes the internal scroll container ref for external consumers */
  scrollRef?: React.RefObject<HTMLDivElement | null>;
}

const PULL_THRESHOLD = 80;
const MAX_PULL = 120;

export function PullToRefresh({ onRefresh, children, className, scrollClassName, scrollRef }: PullToRefreshProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const internalRef = useRef<HTMLDivElement>(null);
  const containerRef = scrollRef || internalRef;
  
  // Callback ref to satisfy motion.div typing while populating our ref
  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
  }, [containerRef]);
  const startY = useRef(0);
  const isDragging = useRef(false);
  
  const pullDistance = useMotionValue(0);
  const indicatorOpacity = useTransform(pullDistance, [0, PULL_THRESHOLD / 2, PULL_THRESHOLD], [0, 0.5, 1]);
  const indicatorScale = useTransform(pullDistance, [0, PULL_THRESHOLD], [0.5, 1]);
  const indicatorRotation = useTransform(pullDistance, [0, PULL_THRESHOLD], [0, 180]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (isRefreshing) return;
    
    const container = containerRef.current;
    if (!container) return;
    
    // Only start pull if at top of scroll
    if (container.scrollTop <= 0) {
      startY.current = e.touches[0].clientY;
      isDragging.current = true;
    }
  }, [isRefreshing]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current || isRefreshing) return;
    
    const container = containerRef.current;
    if (!container || container.scrollTop > 0) {
      isDragging.current = false;
      pullDistance.set(0);
      return;
    }

    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    
    if (diff > 0) {
      // Apply resistance for more natural feel
      const resistance = 0.5;
      const pullValue = Math.min(diff * resistance, MAX_PULL);
      pullDistance.set(pullValue);
      
      // Prevent default scroll when pulling down
      if (pullValue > 10) {
        e.preventDefault();
      }
    }
  }, [isRefreshing, pullDistance]);

  const handleTouchEnd = useCallback(async () => {
    if (!isDragging.current) return;
    isDragging.current = false;

    const currentPull = pullDistance.get();
    
    if (currentPull >= PULL_THRESHOLD && !isRefreshing) {
      triggerHaptic('medium');
      setIsRefreshing(true);
      // Animate to refresh position
      animate(pullDistance, 60, { duration: 0.2 });
      
      try {
        // Safety timeout to prevent infinite spinner
        await Promise.race([
          onRefresh(),
          new Promise(resolve => setTimeout(resolve, 8000)),
        ]);
        triggerHaptic('success');
      } finally {
        setIsRefreshing(false);
        animate(pullDistance, 0, { duration: 0.3, ease: 'easeOut' });
      }
    } else {
      // Snap back
      animate(pullDistance, 0, { duration: 0.3, ease: 'easeOut' });
    }
  }, [isRefreshing, onRefresh, pullDistance]);

  return (
    <div className={cn("relative overflow-hidden", className)}>
      {/* Pull indicator */}
      <motion.div 
        className="absolute left-1/2 -translate-x-1/2 z-10 flex items-center justify-center"
        style={{ 
          top: useTransform(pullDistance, (v) => v - 40),
          opacity: indicatorOpacity,
          scale: indicatorScale,
        }}
      >
        <motion.div 
          className={cn(
            "w-10 h-10 rounded-full bg-card border border-border shadow-lg",
            "flex items-center justify-center"
          )}
          style={{ 
            rotate: isRefreshing ? undefined : indicatorRotation 
          }}
        >
          <Loader2 
            className={cn(
              "w-5 h-5 text-primary",
              isRefreshing && "animate-spin"
            )} 
          />
        </motion.div>
      </motion.div>

      {/* Content container */}
      <motion.div
        ref={setContainerRef}
        className={cn("h-full overflow-y-auto", scrollClassName)}
        style={{ y: pullDistance }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </motion.div>
    </div>
  );
}
